<script setup>
import { computed, ref } from 'vue'
import { Link, useForm } from '@inertiajs/vue3'
import { trans } from 'laravel-vue-i18n'
import Button from 'primevue/button'
import Avatar from 'primevue/avatar'
import Checkbox from 'primevue/checkbox'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import { getInitials, timeAgo, formatCzk } from '../../../../helpers.js'
import { useContactsFilters } from '../../composables/useContactsFilters.js'
import { useTagColors } from '../../composables/useTagColors.js'
import ContactsBulkBar from '../../components/ui-components/contacts/ContactsBulkBar.vue'
import ContactsToolbar from '../../components/ui-components/contacts/ContactsToolbar.vue'
import ContactsSortButton from '../../components/ui-components/contacts/ContactsSortButton.vue'
import CreateContactDrawer from '../../components/ui-components/contacts/CreateContactDrawer.vue'

const props = defineProps({
    contacts:         Object,
    contact:          Object,
    isExpanded:       Boolean,
    allTags:          { type: Array,  default: () => [] },
    tagCounts:        { type: Object, default: () => ({}) },
    reservationForms: { type: Array,  default: () => [] },
    newThisMonth:     { type: Number, default: 0 },
    filters:          { type: Object, default: () => ({}) },
})

const {
    search, tags, formIds,
    dateFrom, dateTo, activityFrom, activityTo,
    sortBy, sortDir,
    applyFilters, toggleTag, toggleFormId,
    setSort, setPage, resetFilters,
} = useContactsFilters(props.filters)

// ─── Tag barvy ────────────────────────────────────────────────────

const { getTagColor } = useTagColors()

// ─── Filtr panel ─────────────────────────────────────────────────

const activeFilterCount = computed(() => {
    let n = 0
    if (props.filters.tags?.length)        { n += props.filters.tags.length }
    if (props.filters.form_ids?.length)    { n += props.filters.form_ids.length }
    if (props.filters.date_from || props.filters.date_to)          { n++ }
    if (props.filters.activity_from || props.filters.activity_to)  { n++ }
    return n
})

// ─── Bulk výběr ───────────────────────────────────────────────────

const selectedIds = ref([])

const allSelected = computed(() =>
    props.contacts?.data?.length > 0 &&
    props.contacts.data.every(c => selectedIds.value.includes(c.id))
)

function toggleAll() {
    selectedIds.value = allSelected.value ? [] : props.contacts.data.map(c => c.id)
}

function toggleSelect(id) {
    const idx = selectedIds.value.indexOf(id)
    selectedIds.value = idx === -1
        ? [...selectedIds.value, id]
        : selectedIds.value.filter(i => i !== id)
}

// ─── Bulk akce ────────────────────────────────────────────────────

const tagDialogOpen     = ref(false)
const messageDialogOpen = ref(false)
const newTag            = ref('')

const bulkTagForm     = useForm({ contact_ids: [], tag: '' })
const bulkMessageForm = useForm({ contact_ids: [], message: '', sendAs: 'email' })

function submitBulkTag() {
    bulkTagForm.contact_ids = selectedIds.value
    bulkTagForm.tag = newTag.value
    bulkTagForm.post('/app/contacts/bulk/tag', {
        onSuccess: () => { tagDialogOpen.value = false; selectedIds.value = [] },
    })
}

function submitBulkMessage() {
    bulkMessageForm.contact_ids = selectedIds.value
    bulkMessageForm.post('/app/contacts/bulk/message', {
        onSuccess: () => { messageDialogOpen.value = false; selectedIds.value = [] },
    })
}

function bulkExport(format) {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/app/contacts/bulk/export'
    form.style.display = 'none'
    const csrf = document.createElement('input')
    csrf.name = '_token'
    csrf.value = document.querySelector('meta[name="csrf-token"]')?.content ?? ''
    form.appendChild(csrf)
    const fmt = document.createElement('input')
    fmt.name = 'format'
    fmt.value = format
    form.appendChild(fmt)
    selectedIds.value.forEach(id => {
        const inp = document.createElement('input')
        inp.name = 'contact_ids[]'
        inp.value = id
        form.appendChild(inp)
    })
    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)
}

function doExport(format) {
    const params = new URLSearchParams()
    if (props.filters.search)   { params.set('search', props.filters.search) }
    if (props.filters.tags)     { props.filters.tags.forEach(t => params.append('tags[]', t)) }
    if (props.filters.form_ids) { props.filters.form_ids.forEach(id => params.append('form_ids[]', id)) }
    if (props.filters.date_from){ params.set('date_from', props.filters.date_from) }
    if (props.filters.date_to)  { params.set('date_to', props.filters.date_to) }
    params.set('format', format)
    window.location.href = '/app/contacts/export?' + params.toString()
}

// ─── Sidebar handlers ─────────────────────────────────────────────

function onDateFromUpdate(val)     { dateFrom.value = val;     applyFilters() }
function onDateToUpdate(val)       { dateTo.value = val;       applyFilters() }
function onActivityFromUpdate(val) { activityFrom.value = val; applyFilters() }
function onActivityToUpdate(val)   { activityTo.value = val;   applyFilters() }

// ─── Vytvoření klienta ─────────────────────────────────────────────

const createDrawerVisible = ref(false)
</script>

<template>
    <div class="flex flex-col gap-4 pb-12 -mt-6 relative">

        <!-- Horní lišta -->
        <div class="flex items-center justify-between gap-3">
            <Transition name="bulk-bar" mode="out-in">
                <ContactsBulkBar
                    v-if="selectedIds.length"
                    :selected-count="selectedIds.length"
                    @add-tag="newTag = ''; tagDialogOpen = true"
                    @send-message="bulkMessageForm.message = ''; messageDialogOpen = true"
                    @export-csv="bulkExport('csv')"
                    @export-xlsx="bulkExport('xlsx')"
                    @close="selectedIds = []"
                />
            </Transition>

            <div />

            <ContactsToolbar
                :search="search"
                :filters="filters"
                :active-filter-count="activeFilterCount"
                :all-tags="allTags"
                :tag-counts="tagCounts"
                :reservation-forms="reservationForms"
                @update:search="search = $event"
                @export="doExport"
                @toggle-tag="toggleTag"
                @toggle-form="toggleFormId"
                @update:date-from="onDateFromUpdate"
                @update:date-to="onDateToUpdate"
                @update:activity-from="onActivityFromUpdate"
                @update:activity-to="onActivityToUpdate"
                @reset="resetFilters"
                @create="createDrawerVisible = true"
            />
        </div>

        <CreateContactDrawer v-model:visible="createDrawerVisible" />

        <!-- Empty state -->
        <template v-if="!contacts?.data?.length">
            <div class="w-full p-16 flex flex-col gap-6 justify-center items-center text-center">
                <img src="../../../../../images/glass/Document.png" alt="" class="w-48 h-48 mb-6 mx-auto" />
                <h2 class="text-2xl font-semibold text-gray-700 dark:text-gray-300">
                    {{ (filters.search || filters.tags?.length || filters.form_ids?.length) ? $t('contacts.empty_search_title') : $t('contacts.empty_title') }}
                </h2>
                <p class="text-gray-500 dark:text-gray-400 max-w-md">
                    <template v-if="filters.search || filters.tags?.length || filters.form_ids?.length">
                        {{ $t('contacts.empty_filter_description') }}
                        <Button link :label="$t('contacts.empty_filter_reset')" class="!p-0 !text-base" @click="resetFilters" />.
                    </template>
                    <template v-else>
                        {{ $t('contacts.empty_description') }}
                    </template>
                </p>
            </div>
        </template>

        <template v-else>
            <div class="bg-white dark:bg-black border border-neutral-200 dark:border-neutral-700 rounded-3xl shadow-sm overflow-hidden">
                <table class="w-full table-fixed">
                    <thead>
                        <tr class="border-b border-neutral-200 dark:border-neutral-700">
                            <th class="px-4 py-3 w-4">
                                <Checkbox :model-value="allSelected" binary @change="toggleAll" />
                            </th>
                            <th class="px-3 py-3 w-8" />
                            <th class="px-4 py-3 text-left w-72">
                                <ContactsSortButton :label="$t('contacts.table_name')" field="name" :sort-by="sortBy" :sort-dir="sortDir" @sort="setSort" />
                            </th>
                            <th class="px-4 py-3 text-left w-40 max-lg:hidden">
                                <span class="text-[13px] font-medium text-neutral-400 dark:text-neutral-500">{{ $t('contacts.table_phone') }}</span>
                            </th>
                            <th class="px-4 py-3 text-left w-52 max-md:hidden">
                                <span class="text-[13px] font-medium text-neutral-400 dark:text-neutral-500">{{ $t('contacts.table_tags') }}</span>
                            </th>
                            <th class="px-4 py-3 w-20 max-lg:hidden">
                                <ContactsSortButton :label="$t('contacts.table_activity')" field="last_interaction_at" :sort-by="sortBy" :sort-dir="sortDir" align="right" @sort="setSort" />
                            </th>
                            <th class="px-4 py-3 w-24 max-md:hidden">
                                <ContactsSortButton :label="$t('contacts.table_spend')" field="total_spend" :sort-by="sortBy" :sort-dir="sortDir" align="right" @sort="setSort" />
                            </th>
                            <th class="px-4 py-3 w-24 max-md:hidden">
                                <ContactsSortButton :label="$t('contacts.table_reservations')" field="reservations_count" :sort-by="sortBy" :sort-dir="sortDir" align="right" @sort="setSort" />
                            </th>
                            <th class="px-3 py-3 w-12" />
                        </tr>
                    </thead>
                    <TransitionGroup tag="tbody" name="grid-move">
                        <tr
                            v-for="item in contacts.data"
                            :key="item.id"
                            class="group border-b last:border-b-0 border-neutral-100 dark:border-neutral-800 transition-colors"
                            :class="contact?.id === item.id || selectedIds.includes(item.id)
                                ? 'bg-blue-50 dark:bg-blue-950/20'
                                : 'hover:bg-neutral-50 dark:hover:bg-neutral-900/30'"
                        >
                            <td class="px-4 py-3">
                                <Checkbox
                                    :model-value="selectedIds.includes(item.id)"
                                    binary
                                    class="opacity-0 group-hover:opacity-100 transition-opacity"
                                    :class="selectedIds.includes(item.id) ? '!opacity-100' : ''"
                                    @change="toggleSelect(item.id)"
                                />
                            </td>
                            <td class="px-3 py-3">
                                <Avatar :label="getInitials(item.name)" shape="circle" class="min-w-8" />
                            </td>
                            <td class="px-7 py-3">
                                <div class="overflow-hidden">
                                    <p class="text-[13px] font-bold truncate [overflow-wrap:anywhere]">{{ item.name }}</p>
                                    <p class="text-[13px] opacity-60 truncate">{{ item.email }}</p>
                                </div>
                            </td>
                            <td class="px-4 py-3 max-lg:hidden">
                                <span v-if="item.phone" class="text-[13px] text-neutral-500 dark:text-neutral-400 tabular-nums">{{ item.phone }}</span>
                                <span v-else class="text-[13px] text-neutral-300 dark:text-neutral-600">—</span>
                            </td>
                            <td class="px-4 py-3 max-md:hidden">
                                <div class="flex flex-wrap gap-1.5">
                                    <span
                                        v-for="tag in (item.tags ?? []).slice(0, 3)"
                                        :key="tag"
                                        class="inline-flex items-center gap-1 text-[13px] font-medium px-2 py-0.5 rounded-full"
                                        :style="{ background: getTagColor(tag) + '18', color: getTagColor(tag) }"
                                    >
                                        <span class="w-1.5 h-1.5 rounded-full" :style="{ background: getTagColor(tag) }" />{{ tag }}
                                    </span>
                                    <span v-if="(item.tags ?? []).length > 3" class="text-[13px] text-neutral-400">+{{ item.tags.length - 3 }}</span>
                                </div>
                            </td>
                            <td class="px-4 py-3 text-right max-lg:hidden">
                                <span class="text-[13px] text-neutral-400 text-nowrap dark:text-neutral-500">{{ timeAgo(item.last_interaction_at) ?? '—' }}</span>
                            </td>
                            <td class="px-4 py-3 text-right max-md:hidden">
                                <span v-if="item.total_spend > 0" class="font-bold text-[13px] text-nowrap text-neutral-800 dark:text-neutral-100 tabular-nums">
                                    {{ formatCzk(item.total_spend) }}
                                </span>
                                <span v-else class="text-[13px] text-neutral-300 dark:text-neutral-600">—</span>
                            </td>
                            <td class="px-4 py-3 text-right max-md:hidden">
                                <span class="font-semibold text-[13px] text-neutral-700 dark:text-neutral-200 tabular-nums">{{ item.reservations_count }}</span>
                                <span class="text-[13px] text-neutral-500 dark:text-neutral-400"> {{ $t('common.rez_suffix') }}</span>
                            </td>
                            <td class="px-3 py-3">
                                <Link :href="`/app/contacts/${item.id}`">
                                    <Button icon="pi pi-pencil" rounded text severity="secondary" v-tooltip.bottom="$t('contacts.tooltip_edit')" class="hover:bg-gray-100 dark:hover:bg-gray-700" />
                                </Link>
                            </td>
                        </tr>
                    </TransitionGroup>
                </table>
            </div>

            <!-- Paginace -->
            <div v-if="contacts.last_page > 1" class="flex items-center justify-between">
                <span class="text-[13px] text-neutral-500">{{ contacts.from }}–{{ contacts.to }} z {{ contacts.total }}</span>
                <div class="flex items-center gap-1">
                    <Button
                        icon="pi pi-chevron-left"
                        text severity="secondary" rounded size="small"
                        :disabled="contacts.current_page === 1"
                        @click="setPage(contacts.current_page - 1)"
                    />
                    <template v-for="page in contacts.last_page" :key="page">
                        <Button
                            v-if="page === 1 || page === contacts.last_page || Math.abs(page - contacts.current_page) <= 1"
                            :label="String(page)"
                            :severity="page === contacts.current_page ? undefined : 'secondary'"
                            :text="page !== contacts.current_page"
                            rounded size="small"
                            class="!w-8 !h-8 !text-sm"
                            @click="setPage(page)"
                        />
                        <span
                            v-else-if="page === contacts.current_page - 2 || page === contacts.current_page + 2"
                            class="w-8 h-8 flex items-center justify-center text-neutral-400 text-sm"
                        >…</span>
                    </template>
                    <Button
                        icon="pi pi-chevron-right"
                        text severity="secondary" rounded size="small"
                        :disabled="contacts.current_page === contacts.last_page"
                        @click="setPage(contacts.current_page + 1)"
                    />
                </div>
            </div>
        </template>

    </div>

    <!-- Dialog: Přidat tag -->
    <Dialog v-model:visible="tagDialogOpen" modal :header="$t('contacts.dialog_tag_title')" class="w-96">
        <div class="flex flex-col gap-4 pt-2">
            <InputText v-model="newTag" :placeholder="$t('contacts.dialog_tag_placeholder')" autofocus />
            <div class="flex gap-2 justify-end">
                <Button :label="$t('common.cancel')" severity="secondary" text @click="tagDialogOpen = false" />
                <Button :label="$t('common.add')" :loading="bulkTagForm.processing" :disabled="!newTag.trim()" @click="submitBulkTag" />
            </div>
        </div>
    </Dialog>

    <!-- Dialog: Poslat zprávu -->
    <Dialog v-model:visible="messageDialogOpen" modal :header="$t('contacts.dialog_message_title')" class="w-[500px]">
        <div class="flex flex-col gap-4 pt-2">
            <Select v-model="bulkMessageForm.sendAs" :options="[{ label: $t('contacts.send_as_email'), value: 'email' }, { label: $t('contacts.send_as_sms'), value: 'sms' }]" option-label="label" option-value="value" />
            <Textarea v-model="bulkMessageForm.message" rows="4" :maxlength="bulkMessageForm.sendAs === 'sms' ? 200 : 2000" autofocus />
            <span v-if="bulkMessageForm.sendAs === 'sms'" class="text-[13px] text-neutral-400">{{ $t('contacts.sms_max_chars') }}</span>
            <div class="flex gap-2 justify-end">
                <Button :label="$t('common.cancel')" severity="secondary" text @click="messageDialogOpen = false" />
                <Button :label="$t('common.send')" :loading="bulkMessageForm.processing" :disabled="!bulkMessageForm.message.trim()" @click="submitBulkMessage" />
            </div>
        </div>
    </Dialog>
</template>

<style scoped>
.bulk-bar-enter-active, .bulk-bar-leave-active { transition: all 0.15s ease; }
.bulk-bar-enter-from, .bulk-bar-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
